import { getTrustedXbrliPolicy } from './trusted-types-policy';

// Renders trusted document html (Batavia/pdf2htmlEX/XBRL) into a shadow root on the element with
// hostId, so the document's styles don't leak into the rest of the app and vice versa.
// Also restores three things that pdf2htmlEX-style, pixel-precise markup can't reach across the
// shadow boundary:
// - Bootstrap's global '*, *::before, *::after { box-sizing: border-box }' (styles.scss), which
//   fixed widths on spacer spans etc. rely on.
// - '.c { overflow: hidden }' (pdf2htmlEX's page-edge clip) can clip the last character of a value
//   due to a small remaining difference in character width; never clipping figures outweighs
//   pixel-perfect page clipping.
// - @font-face registration isn't reliable inside a shadow root in every browser, so those rules
//   are also registered at the document level.
export function renderDocumentIntoShadowRoot(hostId: string, html: string): ShadowRoot | null {
  const host = document.getElementById(hostId);
  if (!host) return null;

  registerEmbeddedFontFaces(hostId, html);

  const shadowRoot = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
  const policy = getTrustedXbrliPolicy();
  const appStyle = '<style>'
    + ':host { font-family: var(--bs-body-font-family); font-weight: 500; }'
    + ':host, :host *, :host *::before, :host *::after { box-sizing: border-box; }'
    + ':host .c { overflow: visible; }'
    + '</style>';
  const scopedHtml = appStyle + html;
  shadowRoot.innerHTML = policy ? policy.createHTML(scopedHtml) : scopedHtml;

  shadowRoot.querySelectorAll('img').forEach(img => {
    img.addEventListener('error', () => img.remove());
  });

  return shadowRoot;
}

function registerEmbeddedFontFaces(hostId: string, html: string): void {
  const styleId = 'pdf2htmlex-fontfaces-' + hostId;
  if (document.getElementById(styleId)) return;
  const fontFaceBlocks = html.match(/@font-face\s*\{[^}]*\}/g);
  if (!fontFaceBlocks) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = fontFaceBlocks.join('\n');
  document.head.appendChild(style);
}
